/**
 * 2048 Board Component
 *
 * 4×4 grid with tile rendering, slide animations (CSS transitions),
 * merge pop effect, and new tile scale-up animation.
 * Responsive: fits mobile screens.
 */

import React from 'react';
import {
  Board2048 as Board2048Type,
  Board2048Props,
  Tile,
  TILE_COLORS,
  TILE_TEXT_COLORS,
  DEFAULT_TILE_TEXT_COLOR,
} from '../types';
import './Board2048.css';

const Board2048: React.FC<Board2048Props> = ({
  board,
  tiles,
  boardSize,
  disabled = false,
}) => {
  const getTileStyle = (tile: Tile): React.CSSProperties => {
    const bgColor = TILE_COLORS[tile.value] || '#3c3a32';
    const textColor = TILE_TEXT_COLORS[tile.value] || DEFAULT_TILE_TEXT_COLOR;

    return {
      '--tile-row': tile.row,
      '--tile-col': tile.col,
      backgroundColor: bgColor,
      color: textColor,
    } as React.CSSProperties;
  };

  const getTileClassName = (tile: Tile): string => {
    const classes = ['board-2048__tile'];

    if (tile.mergedFrom) {
      classes.push('board-2048__tile--merged');
    }

    if (tile.isNew) {
      classes.push('board-2048__tile--new');
    }

    // Font size classes based on digit count
    if (tile.value >= 1000) {
      classes.push('board-2048__tile--four-digits');
    } else if (tile.value >= 100) {
      classes.push('board-2048__tile--three-digits');
    }

    if (tile.value >= 2048) {
      classes.push('board-2048__tile--super');
    }

    return classes.join(' ');
  };

  // Render grid background cells
  const renderGridCells = () => {
    const cells = [];
    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        cells.push(
          <div
            key={`cell-${r}-${c}`}
            className="board-2048__cell"
          />
        );
      }
    }
    return cells;
  };

  return (
    <div
      className={`board-2048 board-2048--size-${boardSize} ${disabled ? 'board-2048--disabled' : ''}`}
      role="grid"
      aria-label="2048 game board"
      style={{ '--board-size': boardSize } as React.CSSProperties}
    >
      {/* Background grid */}
      <div className="board-2048__grid">
        {renderGridCells()}
      </div>

      {/* Tile layer */}
      <div className="board-2048__tiles">
        {tiles.map((tile) => (
          <div
            key={tile.id}
            className={getTileClassName(tile)}
            style={getTileStyle(tile)}
            role="gridcell"
            aria-label={`Tile with value ${tile.value}`}
          >
            {tile.value}
          </div>
        ))}
      </div>
    </div>
  );
};

export { Board2048 };
export default Board2048;
